import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Box, TextField, MenuItem, FormControl, InputLabel, Select, Typography, Button as PriButton , IconButton, Grid, Tooltip } from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon, ArrowBackOutlined } from '@mui/icons-material';
import { Wrapper } from "../SettingsSection/SettingsSection.styles";
import { HeaderContainer, SectionTitle } from "./OpCoOnboardForm.styles";
import { OPCO_REGEX, OPCO_VALIDATION_MESSAGES, FEED_MENU_ITEMS } from '../../constants/opCoConstants';
import { downloadOpcoTemplate, uploadOpcoTemplate } from '../../hooks/OpCo';
import useShowError from "../../hooks/useShowError";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import Button from "../Button/Button"
import useShowToast from "../../hooks/useShowToast"
import { ERROR_MESSAGES } from '../../constants/opCoConstants'
import { useAppSelector } from '../../app/hooks';
import { selectLGInternalUser } from '../../activeScreenSlice';

export interface IOpCoFormData {
    opcoConfigurationMode?: string;
    slug?: string;
    name: string;
    description: string;
    opco_code?: string;
    invoiceemailid: string;
    rejectionemailtype: string;
    rejectemailid: string;
    unprocessedemailid: string;
    transmittingsummaryinfo: string;
    po_feed: string;
    invoice_feed: string;
    supplier_feed: string;
    reconcilliation_feed: string;
    sla_invoiceprocessingdays: string;
    sla_invoiceoverduenotificationdays: string;
    invoiceretention: string;
    dataretention: string;
    worklistname: string;
    teamname: string;
    emailprovider: string;
    erpprovider: string;
    additionalsystems: 'Yes' | 'No' | '';
    additionalsystemsdetails: string;
    id?: number; 
}

type OpCoFormProps = {
    mode: 'create' | 'edit';
    initialData: IOpCoFormData;
    onSubmit: (data: IOpCoFormData) => void;
    onCancel: () => void;
    loading?: boolean;
};
interface IFormErrors {
    [key: string]: string | undefined;
}

export default function OpCoForm({
    mode,
    initialData,
    onSubmit,
    onCancel,
    loading = false
}: OpCoFormProps) {
    const [formData, setFormData] = useState<IOpCoFormData>(initialData);
    const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
    const [openForm, setOpenForm] = useState('singleUpload');
    const [fileName, setFileName] = useState('');
    const [bulkError, setBulkError] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const downloadOpcoTemplateMutation = downloadOpcoTemplate();
    const uploadOpcoTemplateMutation = uploadOpcoTemplate();
    const [file, setFile] = useState(null);
    const showError = useShowError();
    const showToast = useShowToast();
    const LGInternalUser = useAppSelector(selectLGInternalUser);

    const handleBrowseClick = () => {
        // Clear the input value
        if (inputRef.current) {
            inputRef.current.value = null;
        }
    };

    const validate = (formData: IOpCoFormData): IFormErrors => {
        const errors: IFormErrors = {};
        if (!formData.description) {
            errors.description = OPCO_VALIDATION_MESSAGES.REQUIRED;
        } else if (formData.description.length > 50) {
            errors.description = OPCO_VALIDATION_MESSAGES.MAX_50;
        } else if (!OPCO_REGEX.ALPHANUMERICSPACE.test(formData.description)) {
            errors.description = OPCO_VALIDATION_MESSAGES.ALPHANUMERICSPACE;
        }

        if (!formData.invoiceemailid) {
            errors.invoiceemailid = OPCO_VALIDATION_MESSAGES.EMAIL_REQUIRED;
        } else if (!OPCO_REGEX.EMAIL.test(formData.invoiceemailid) || formData.invoiceemailid.includes("..")) {
            errors.invoiceemailid = OPCO_VALIDATION_MESSAGES.INVALID_EMAIL;
        } else if (formData.invoiceemailid.trim().length > 50) {
            errors.invoiceemailid = OPCO_VALIDATION_MESSAGES.MAX_50;
        }

        if (!formData.rejectionemailtype) { errors.rejectionemailtype = OPCO_VALIDATION_MESSAGES.REQUIRED; }

        if (formData.rejectionemailtype?.toLowerCase() === 'custom') {
            if (!formData.rejectemailid) {
                errors.rejectemailid = OPCO_VALIDATION_MESSAGES.REQUIRED;
            } else if (!OPCO_REGEX.EMAIL.test(formData.rejectemailid) || formData.rejectemailid.includes("..")) {
                errors.rejectemailid = OPCO_VALIDATION_MESSAGES.INVALID_EMAIL;
            } else if (formData.rejectemailid.trim().length > 50) {
                errors.rejectemailid = OPCO_VALIDATION_MESSAGES.MAX_50;
            }
        }

        if (!formData.po_feed) errors.po_feed = OPCO_VALIDATION_MESSAGES.REQUIRED;
        if (!formData.supplier_feed) errors.supplier_feed = OPCO_VALIDATION_MESSAGES.REQUIRED;
        if (!formData.invoice_feed) errors.invoice_feed = OPCO_VALIDATION_MESSAGES.REQUIRED;
        if (!formData.reconcilliation_feed) errors.reconcilliation_feed = OPCO_VALIDATION_MESSAGES.REQUIRED;
        if (!formData.transmittingsummaryinfo) errors.transmittingsummaryinfo = OPCO_VALIDATION_MESSAGES.REQUIRED;

        const numericFields = [
            'sla_invoiceprocessingdays', 'sla_invoiceoverduenotificationdays', 'invoiceretention', 'dataretention'
        ];
        numericFields.forEach(field => {
            const value = formData[field as keyof IOpCoFormData];
            if (!value) {
                errors[field] = OPCO_VALIDATION_MESSAGES.REQUIRED;
            } else if (isNaN(Number(value)) || Number(value) < 0 || !Number.isInteger(Number(value))) {
                errors[field] = OPCO_VALIDATION_MESSAGES.NON_NEGATIVE_INT;
            }
        });

        if (!formData.worklistname) {
            errors.worklistname = OPCO_VALIDATION_MESSAGES.REQUIRED;
        } else if (!OPCO_REGEX.ALPHANUMERICSPACE.test(formData.worklistname)) {
            errors.worklistname = OPCO_VALIDATION_MESSAGES.ALPHABETICSPACE;
        } else if (formData.worklistname.length > 20) {
            errors.worklistname = OPCO_VALIDATION_MESSAGES.MAX_20;
        }
        if (!formData.teamname) {
            errors.teamname = OPCO_VALIDATION_MESSAGES.REQUIRED;
        } else if (!OPCO_REGEX.ALPHANUMERICSPACE.test(formData.teamname)) {
            errors.teamname = OPCO_VALIDATION_MESSAGES.ALPHABETICSPACE;
        } else if (formData.teamname.length > 20) {
            errors.teamname = OPCO_VALIDATION_MESSAGES.MAX_20;
        }

        if (!formData.unprocessedemailid) {
            errors.unprocessedemailid = OPCO_VALIDATION_MESSAGES.REQUIRED;
        } else if (!OPCO_REGEX.EMAIL.test(formData.unprocessedemailid) || formData.unprocessedemailid.includes("..")) {
            errors.unprocessedemailid = OPCO_VALIDATION_MESSAGES.INVALID_EMAIL;
        } else if (formData.unprocessedemailid.trim().length > 50) {
            errors.unprocessedemailid = OPCO_VALIDATION_MESSAGES.MAX_50;
        }

        // Providers and Systems Validations
        if (!formData.emailprovider) errors.emailprovider = OPCO_VALIDATION_MESSAGES.REQUIRED;
        if (!formData.erpprovider) errors.erpprovider = OPCO_VALIDATION_MESSAGES.REQUIRED;
        if (!formData.additionalsystems) errors.additionalsystems = OPCO_VALIDATION_MESSAGES.REQUIRED;

        if (formData.additionalsystems === 'Yes' && !formData.additionalsystemsdetails) {
            errors.additionalsystemsdetails = OPCO_VALIDATION_MESSAGES.ADD_SYS_DETAILS;
        }

        if (formData.invoiceemailid && formData.unprocessedemailid && formData.invoiceemailid.trim().toLowerCase() === formData.unprocessedemailid.trim().toLowerCase()) 
            {
            errors.invoiceemailid = "Email id cannot be same";
            errors.unprocessedemailid = "Email id cannot be same";
            }

        return errors;
    };

    const validationErrors = useMemo(() => validate(formData), [formData]);

    const handleChange = useCallback((e: any) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setTouchedFields((prev) => new Set(prev).add(name));
    }, []);

    const handleSave = useCallback(() => {
        const errors = validate(formData);
        setTouchedFields(new Set(Object.keys(formData)));
        if (Object.keys(errors).length === 0) {
            onSubmit(formData);
        }
    }, [formData, onSubmit]);

    const handleMenuItemClick = (form) => {
        setOpenForm(form);
    };

    const handleBulkCancel = () => {
        setOpenForm("singleUpload")
        setFormData((prev) => ({ ...prev, ["opcoConfigurationMode"]: "single" }));
    }

    const handleBulkSave = (event) => {
        event.preventDefault();
        setBulkError(!file);
        if (!file) {
            return;
        }
        uploadOpcoTemplateMutation.mutate(file, {
            onSuccess(){
                showToast(`File "${inputRef.current.files[0].name}" uploaded successfully !`);
            },
            onError(error:any) {
                if (error?.code === "ERR_NETWORK") {
                    showError(ERROR_MESSAGES.NETWORK);
                } else if (error) {
                    if (error?.body?.detail?.length > 0) {
                        showError(`${error.body.detail}`)
                    }
                    else {
                        showError(ERROR_MESSAGES.SERVER_500)
                    }
                }
            }
        });

    }

    const handleCancel = useCallback(() => {
        setFormData(initialData);
        setTouchedFields(new Set());
        onCancel();
    }, [onCancel, initialData]);

    const handleBrwBtnClick = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files[0];
        setFileName(file ? file.name : '');
        setFile(file || null);
        if (event.target.files[0]) setBulkError(false);
    };

    const handleDownload = () => {
        downloadOpcoTemplateMutation.mutate(undefined,{
            onSuccess: async (response) => {
                showToast("Template has been successfully downloaded.")
                const blob = response;
                let filename = "Business Unit Onboarding Template.xlsx";
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            },
            onError: () => {
               showError("Something went wrong, please try again.")
            },
        })
    };

    const hasError = useCallback((fieldName: string) => touchedFields.has(fieldName) && !!validationErrors[fieldName], [touchedFields, validationErrors]);
    const getErrorMessage = useCallback((fieldName: string) => touchedFields.has(fieldName) ? validationErrors[fieldName] : undefined, [touchedFields, validationErrors]);

    return (
        <Wrapper>
            <HeaderContainer>
                <Box>
                    <Typography variant="h5" sx={{ fontSize: '24px', fontWeight: 700, color: '#111'}}>
                        {mode === 'create' ? "Create Business Unit" : "Edit Business Unit"}
                    </Typography>
                </Box>
                <PriButton
                    variant="outlined"
                    startIcon={<ArrowBackOutlined />}
                    onClick={handleCancel}
                >
                    Existing Business Units
                </PriButton>
            </HeaderContainer>
            {mode === "create" && (
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FormControl
                            fullWidth
                            size="small"
                            variant="outlined"
                            error={hasError('opcoConfigurationMode')}
                            sx={{ height: '40px' }}
                        >
                            <InputLabel id="opco-configuration-mode-label">Configuration Mode</InputLabel>
                            <Select
                                labelId="opco-configuration-mode-label"
                                name="opcoConfigurationMode"
                                value={formData.opcoConfigurationMode}
                                label="Configuration Mode"
                                onChange={handleChange}
                                disabled = {LGInternalUser}
                                onClose={() =>
                                    setTouchedFields((prev) => new Set(prev).add('opcoConfigurationMode'))
                                }
                            >
                                <MenuItem onClick={() => handleMenuItemClick('singleUpload')} value="single">Single Upload</MenuItem>
                                <MenuItem onClick={() => handleMenuItemClick('bulkUpload')} value="bulk">Bulk Upload</MenuItem>

                            </Select>
                            {hasError('opcoConfigurationMode') && (
                                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                    {getErrorMessage('opcoConfigurationMode')}
                                </Typography>
                            )}
                        </FormControl>
                            <Tooltip title="Select BU Configuration Method: Single or Bulk Upload" arrow>
                                <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                            </Tooltip>
                    </Grid>
                </Grid>
            )}
            <Box sx={{ display: openForm === "bulkUpload"? "none": "" }}>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={6} sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                        <TextField
                            label="Name"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            placeholder="Input text"
                            variant="outlined"
                            error={hasError('description')}
                            helperText={getErrorMessage('description')}
                            disabled = {LGInternalUser}
                            onBlur={() => setTouchedFields((prev) => new Set(prev).add('description'))}
                            sx={{ height: '40px', width: '545px' }}
                        />
                    </Grid>
                    {mode === 'edit' && (
                        <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <TextField
                                label="Code (Autogenerated)"
                                name="opco_code"
                                value={formData.opco_code}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                disabled
                                variant="outlined"
                                sx={{ width: "300px" }}
                            />
                        </Grid>
                    )}

                </Grid>
                <Box sx={{ display: 'flex' }}>
                    <Grid sx={{ marginBottom: '15px' }}><SectionTitle>Email Settings</SectionTitle></Grid>
                </Box>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FormControl fullWidth size="small" variant="outlined" sx={{ height: '40px' }} error={hasError('emailprovider')}>
                            <InputLabel id='email-provider-label'>Email Provider</InputLabel>
                            <Select
                                labelId='email-provider-label'
                                name="emailprovider"
                                value={formData.emailprovider?.toLowerCase()}
                                onChange={handleChange}
                                label="Email Provider"
                                disabled = {LGInternalUser}
                                onClose={() => setTouchedFields((prev) => new Set(prev).add('emailprovider'))}
                            >
                                <MenuItem value="gmail">Gmail</MenuItem>
                                <MenuItem value="outlook365">Outlook365</MenuItem>
                            </Select>
                            {hasError('emailprovider') && (
                                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                    {getErrorMessage('emailprovider')}
                                </Typography>
                            )}
                        </FormControl>
                        <Tooltip title="Select the email service provider that will be used to receive your invoices (e.g., O365, Gmail)." arrow>
                            <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <TextField
                            label="Invoice Email" 
                            name="invoiceemailid"
                            value={formData.invoiceemailid}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            placeholder="Input text"
                            variant="outlined"
                            error={hasError('invoiceemailid')}
                            helperText={getErrorMessage('invoiceemailid')}
                            disabled = {LGInternalUser}
                            onBlur={() => setTouchedFields((prev) => new Set(prev).add('invoiceemailid'))}
                            sx={{ height: '40px' }}
                        />
                        <Tooltip title="Enter the email address where invoices will be sent." arrow>
                            <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ textAlign: 'center' }}>
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FormControl
                            fullWidth
                            size="small"
                            variant="outlined"
                            error={hasError('rejectionemailtype')}
                            sx={{ height: '40px' }}
                        >
                            <InputLabel id="rejection-mail-address-label">Rejection Recipient</InputLabel>
                            <Select
                                labelId="rejection-mail-address-label"
                                name="rejectionemailtype"
                                value={formData.rejectionemailtype?.trim().replace(/address|\s+/gi, '').toLowerCase()}
                                label="Rejection Recipient"
                                onChange={handleChange}
                                disabled = {LGInternalUser}
                                inputProps={{ 'aria-label': 'Rejection Email Address' }}
                                onClose={() =>
                                    setTouchedFields((prev) => new Set(prev).add('rejectionemailtype'))
                                }
                            >
                                <MenuItem value="supplieremail">Supplier Email Address</MenuItem>
                                <MenuItem value="senderemail">Sender Email Address</MenuItem>
                                <MenuItem value="custom">Custom Recipient</MenuItem>
                            </Select>
                            {hasError('rejectionemailtype') && (
                                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                    {getErrorMessage('rejectionemailtype')}
                                </Typography>
                            )}
                        </FormControl>
                        <Tooltip title="Select who should receive the email notification when an invoice is rejected. Choose from Sender, Supplier, or specify a Custom recipient" arrow>
                            <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                    {formData.rejectionemailtype?.toLowerCase() === 'custom' && (
                        <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <TextField
                                label="Custom Recipient"
                                name="rejectemailid"
                                value={formData.rejectemailid}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                error={hasError('rejectemailid')}
                                helperText={getErrorMessage('rejectemailid')}
                                sx={{ height: '40px' }}
                                onBlur={() => setTouchedFields((prev) => new Set(prev).add('rejectemailid'))}
                            />
                            <Tooltip title="Enter the email address to receive invoice rejection notifications if you selected “Custom” as the recipient." arrow>
                                <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                            </Tooltip>
                        </Grid>
                    )}
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <TextField
                            label="Unprocessed Email"
                            name="unprocessedemailid"
                            value={formData.unprocessedemailid}
                            onChange={handleChange}
                            fullWidth
                            sx={{ height: '40px' }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            placeholder="Input text"
                            variant="outlined"
                            disabled = {LGInternalUser}
                            error={hasError('unprocessedemailid')}
                            helperText={getErrorMessage('unprocessedemailid')}
                            onBlur={() => setTouchedFields((prev) => new Set(prev).add('unprocessedemailid'))}
                        />
                        <Tooltip title="Enter the email address where notifications about unprocessed invoices should be sent" arrow>
                            <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex' }}>
                    <Grid sx={{ marginBottom: '15px' }}>
                        <SectionTitle sx={{display: "inline-flex"}}>Data Feed Method</SectionTitle>
                        <Tooltip title="Select how you will send or receive data feeds (incoming or outgoing) with us—choose CSV files, APIs, or both." arrow>
                            <IconButton size="small" sx={{ p: 0.4 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                </Box>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FormControl fullWidth size="small" variant="outlined" sx={{ height: '40px' }} error={hasError('po_feed')}>
                            <InputLabel id='method-forpo-feed-label'>Purchase Order Feed</InputLabel>
                            <Select
                                labelId='method-forpo-feed-label'
                                name="po_feed"
                                value={formData.po_feed?.replace(/\s+/g, '').toLowerCase()}
                                onChange={handleChange}
                                label="Purchase Order Feed"
                                inputProps={{ 'aria-label': 'Method for PO Feed' }}
                                disabled = {LGInternalUser}
                                onClose={() => setTouchedFields((prev) => new Set(prev).add('po_feed'))}
                            >
                                {
                                    FEED_MENU_ITEMS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)
                                }
                            </Select>
                            {hasError('po_feed') && (
                                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                    {getErrorMessage('po_feed')}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FormControl fullWidth size="small" variant="outlined" sx={{ height: '40px' }} error={hasError('supplier_feed')}>
                            <InputLabel id="method-supplier-feed-label"  >Supplier Data Feed</InputLabel>
                            <Select
                                labelId="method-supplier-feed-label"
                                label="Supplier Data Feed"
                                name="supplier_feed"
                                value={formData.supplier_feed?.replace(/\s+/g, '').toLowerCase()}
                                onChange={handleChange}
                                disabled = {LGInternalUser}
                                inputProps={{ 'aria-label': 'Method for Supplier Feed label' }}
                                onClose={() => setTouchedFields((prev) => new Set(prev).add('supplier_feed'))}

                            >
                                {
                                    FEED_MENU_ITEMS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)
                                }
                            </Select>
                            {hasError('supplier_feed') && (
                                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                    {getErrorMessage('supplier_feed')}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ textAlign: 'center' }}>
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FormControl fullWidth size="small" variant="outlined" sx={{ height: '40px' }} error={hasError('invoice_feed')}>
                            <InputLabel id='method-for-invoice-sta-label'>Invoice Data Feed</InputLabel>
                            <Select
                                labelId='method-for-invoice-sta-label'
                                name="invoice_feed"
                                value={formData.invoice_feed?.replace(/\s+/g, '').toLowerCase()}
                                onChange={handleChange}
                                label="Invoice Data Feed"
                                disabled = {LGInternalUser}
                                inputProps={{ 'aria-label': 'Method for Invoice Status Feed label', style: { paddingRight: '62px' } }}
                                onClose={() => setTouchedFields((prev) => new Set(prev).add('invoice_feed'))}
                            >
                                {
                                    FEED_MENU_ITEMS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)
                                }
                            </Select>
                            {hasError('invoice_feed') && (
                                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                    {getErrorMessage('invoice_feed')}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FormControl fullWidth size="small" variant="outlined" sx={{ height: '40px' }} error={hasError('reconcilliation_feed')}>
                            <InputLabel id='invoice-recon-feed-label'>Reconciliation Feed (Outgoing)</InputLabel>
                            <Select
                                labelId='invoice-recon-feed-label'
                                name="reconcilliation_feed"
                                value={formData.reconcilliation_feed?.replace(/\s+/g, '').toLowerCase()}
                                onChange={handleChange}
                                label="Reconciliation Feed (Outgoing)"
                                disabled = {LGInternalUser}
                                inputProps={{ 'aria-label': 'Method for Invoice Reconciliation Feed label' }}
                                onClose={() => setTouchedFields((prev) => new Set(prev).add('reconcilliation_feed'))}
                            >
                                {
                                    FEED_MENU_ITEMS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)
                                }
                            </Select>
                            {hasError('reconcilliation_feed') && (
                                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                    {getErrorMessage('reconcilliation_feed')}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid item md={3.5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FormControl fullWidth size="small" variant="outlined" sx={{ height: '40px' }} error={hasError('transmittingsummaryinfo')}>
                            <InputLabel id='trans-summary-info'>Reporting Feed (Outgoing)</InputLabel>
                            <Select
                                labelId='trans-summary-info'
                                name="transmittingsummaryinfo"
                                value={formData.transmittingsummaryinfo?.replace(/\s+/g, '').toLowerCase()}
                                onChange={handleChange}
                                label="Reporting Feed (Outgoing)"
                                disabled = {LGInternalUser}
                                inputProps={{ 'aria-label': 'Method for Transmitting Summary Information label' }}
                                onClose={() => setTouchedFields((prev) => new Set(prev).add('transmittingsummaryinfo'))}
                            >
                                {
                                    FEED_MENU_ITEMS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)
                                }
                            </Select>
                            {hasError('transmittingsummaryinfo') && (
                                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                    {getErrorMessage('transmittingsummaryinfo')}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                </Grid>
                <Grid sx={{ marginBottom: '15px' }}><SectionTitle>Invoice SLAs and Data Retention</SectionTitle></Grid>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <TextField
                            label="Overdue Invoice SLA (Business Days)"
                            name="sla_invoiceprocessingdays"
                            value={formData.sla_invoiceprocessingdays}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            placeholder="Input Number of Days"
                            variant="outlined"
                            error={hasError('sla_invoiceprocessingdays')}
                            sx={{ height: '40px' }}
                            helperText={getErrorMessage('sla_invoiceprocessingdays')}
                            disabled = {LGInternalUser}
                            onBlur={() => setTouchedFields((prev) => new Set(prev).add('sla_invoiceprocessingdays'))}
                        />
                        <Tooltip title="Enter the number of business days after which an unprocessed invoice is marked as ‘Overdue’ on the UI." arrow>
                            <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <TextField
                            label="Notification Delay After Overdue (Business Days)"
                            name="sla_invoiceoverduenotificationdays"
                            value={formData.sla_invoiceoverduenotificationdays}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            placeholder="Input Number of Days"
                            variant="outlined"
                            disabled = {LGInternalUser}
                            error={hasError('sla_invoiceoverduenotificationdays')}
                            sx={{ height: '40px' }}
                            helperText={getErrorMessage('sla_invoiceoverduenotificationdays')}
                            onBlur={() => setTouchedFields((prev) => new Set(prev).add('sla_invoiceoverduenotificationdays'))}
                        />
                        <Tooltip title="Specify the number of business days after an invoice is marked overdue before a notification is sent." arrow>
                            <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ textAlign: 'center' }}>
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <TextField
                            label="Invoice Image Retention Period (Calendar Days)"
                            name="invoiceretention"
                            value={formData.invoiceretention}
                            onChange={handleChange}
                            fullWidth
                            disabled = {LGInternalUser}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            placeholder="Input Number of Days"

                            sx={{ height: '40px' }} variant="outlined"
                            error={hasError('invoiceretention')}
                            helperText={getErrorMessage('invoiceretention')}
                            onBlur={() => setTouchedFields((prev) => new Set(prev).add('invoiceretention'))}
                        />
                        <Tooltip title="Indicate the number of calendar days to retain invoice images before they are archived." arrow>
                            <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <TextField
                            label="Data Archival & Deletion Period (Months)"
                            name="dataretention"
                            value={formData.dataretention}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ height: '40px' }}
                            placeholder="Input Number of Months"
                            variant="outlined"
                            disabled = {LGInternalUser}
                            error={hasError('dataretention')}
                            helperText={getErrorMessage('dataretention')}
                            onBlur={() => setTouchedFields((prev) => new Set(prev).add('dataretention'))}
                        />
                        <Tooltip title="Set the number of months after which invoice data is archived and deleted from the production database." arrow>
                            <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
               <Grid sx={{ marginBottom: '15px' }}><SectionTitle>Default Assignment Settings</SectionTitle></Grid>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <TextField
                            label="Team Name"
                            name="teamname"
                            value={formData.teamname}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            sx={{ height: '40px' }}
                            InputLabelProps={{ shrink: true }}
                            placeholder="Input text"
                            variant="outlined"
                            disabled = {LGInternalUser}
                            error={hasError('teamname')}
                            helperText={getErrorMessage('teamname')}
                            onBlur={() => setTouchedFields((prev) => new Set(prev).add('teamname'))}
                        />
                    </Grid>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <TextField
                            label="Worklist Name"
                            name="worklistname"
                            value={formData.worklistname}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            placeholder="Input text"
                            sx={{ height: '40px' }}
                            disabled = {LGInternalUser}
                            variant="outlined"
                            error={hasError('worklistname')}
                            helperText={getErrorMessage('worklistname')}
                            onBlur={() => setTouchedFields((prev) => new Set(prev).add('worklistname'))}
                        />
                    </Grid>

                </Grid>

                <Grid sx={{ marginBottom: '15px' }}><SectionTitle>Additional Information</SectionTitle></Grid>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FormControl fullWidth size="small" variant="outlined" sx={{ height: '40px' }} error={hasError('erpprovider')}>
                            <InputLabel id='erp-provider-label'>ERP Provider</InputLabel>
                            <Select
                                labelId='erp-provider-label'
                                name="erpprovider"
                                value={formData.erpprovider?.toLowerCase()}
                                onChange={handleChange}
                                label="ERP Provider"
                                disabled = {LGInternalUser}
                                onClose={() => setTouchedFields((prev) => new Set(prev).add('erpprovider'))}
                            >
                                <MenuItem value="sap">SAP</MenuItem>
                                <MenuItem value="oracle">Oracle</MenuItem>
                                <MenuItem value="netsuite">NetSuite</MenuItem>
                                <MenuItem value="other">other</MenuItem>
                            </Select>
                            {hasError('erpprovider') && (
                                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                    {getErrorMessage('erpprovider')}
                                </Typography>
                            )}
                        </FormControl>
                        <Tooltip title="Enter the name of the main ERP (Enterprise Resource Planning) provider used by your organization." arrow>
                            <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={6} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Typography variant="body1" sx={{ textAlign: 'center' }}>
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FormControl fullWidth size="small" variant="outlined" sx={{ height: '40px' }} error={hasError('additionalsystems')}>
                            <InputLabel id='additional-systems-label'>Additional Systems</InputLabel>
                            <Select
                                labelId='additional-systems-label'
                                name="additionalsystems"
                                value={formData.additionalsystems}
                                onChange={handleChange}
                                label="Additional Systems"
                                disabled = {LGInternalUser}
                                onClose={() => setTouchedFields((prev) => new Set(prev).add('additionalsystems'))}
                            >
                                <MenuItem value="Yes">Yes</MenuItem>
                                <MenuItem value="No">No</MenuItem>
                            </Select>
                            {hasError('additionalsystems') && (
                                <Typography variant="caption" color="error" sx={{ ml: 1.5, mt: 0.5 }}>
                                    {getErrorMessage('additionalsystems')}
                                </Typography>
                            )}
                        </FormControl>
                        <Tooltip title="List any other systems integrated with your ERP (e.g., CRM, HRMS, or financial tools)." arrow>
                            <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item md={3} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <TextField
                            label="System Details"
                            name="additionalsystemsdetails"
                            value={formData.additionalsystemsdetails}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            placeholder="Input text"
                            variant="outlined"
                            sx={{ height: '40px' }}
                            disabled={formData.additionalsystems === 'No' || LGInternalUser}
                            error={hasError('additionalsystemsdetails')}
                            helperText={getErrorMessage('additionalsystemsdetails')}
                            onBlur={() => setTouchedFields((prev) => new Set(prev).add('additionalsystemsdetails'))}
                        />
                        <Tooltip title="Provide further information or descriptions for each additional system listed above, including purpose and integration points." arrow>
                            <IconButton size="small" sx={{ p: 0.5 }}><InfoOutlinedIcon fontSize="small" sx={{ color: '#888' }} /></IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mt: 4 }}>
                    <PriButton
                        variant="outlined"
                        onClick={handleCancel}
                    >
                        Cancel
                    </PriButton>
                    <PriButton
                        variant="contained"
                        onClick={handleSave}
                        disabled={loading || Object.keys(validationErrors).length > 0 || LGInternalUser}
                    >
                        {mode === 'create' ? "Create Business Unit" : "Save"}
                    </PriButton>
                    
                </Box>
            </Box>
            <Box sx={{ display: openForm === "singleUpload"? "none": "" }}>
                <Grid container sx={{ marginBottom: '40px' }}>
                    <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <PriButton variant="contained" color="secondary" onClick={handleDownload} startIcon={<FileDownloadOutlinedIcon/>}>
                            Download template
                        </PriButton>
                    </Grid>
                </Grid>
                <Grid container sx={{ marginBottom: '20px' }}>
                    <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <FormControl
                            fullWidth
                            size="small"
                            variant="outlined"
                            sx={{ height: '40px' }}
                        >
                            <TextField
                                label="File path"
                                variant="outlined"
                                value={fileName}
                                InputProps={{
                                    readOnly: true,
                                }}
                                size="small"
                                sx={{ minWidth: 500 }}
                                error={!!bulkError}
                                helperText={bulkError ? "Please select a file before submitting." : ""}
                            />
                            
                            <input
                                type="file"
                                hidden
                                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                required
                                ref={inputRef}
                                onChange={handleFileChange}
                                onClick={handleBrowseClick}
                            />
                        </FormControl>
                        <PriButton sx={{marginLeft: '20px'}} variant="outlined" onClick={handleBrwBtnClick} startIcon={<FileUploadOutlinedIcon />}>
                            Browse
                        </PriButton>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 2, mt: 4 }}>
                    <PriButton
                        variant="contained"
                        onClick={handleBulkSave}
                        disabled={loading}
                    >
                        Save
                    </PriButton>
                    <PriButton
                        variant="outlined"
                        onClick={handleBulkCancel}
                    >
                        Cancel
                    </PriButton>
                </Box>
            </Box>

        </Wrapper>
    );
}
